/**
 * =============================================================================
 * Componente: AffiliateProgramsBreakdownCard
 * Versão: 2.0.0 (Server-side hardening)
 *
 * Mudanças v2.0.0:
 *   - Os dados podem agora vir via prop `programs` (recomendado: do hook
 *     `useAffiliateHomeOverview`). Quando a prop não é passada, faz fallback
 *     para o overview internamente.
 *   - Saldos exibidos vêm do CÁLCULO server-side (ledger), nunca de coluna
 *     agregada. O componente apenas renderiza.
 * =============================================================================
 */
import { Wallet, TrendingUp, CheckCircle2, AlertCircle, Pause, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAffiliateHomeOverview,
  type HomeOverviewProgram,
} from '@/hooks/useAffiliateHomeOverview';
import { KpiInfoTooltip } from '@/components/affiliate/shared/KpiInfoTooltip';
import { cn } from '@/lib/utils';

function fmtBRL(value: number): string {
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function fmtInt(value: number): string {
  return Number(value).toLocaleString('pt-BR');
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Ativo',
  pending: 'Aguardando aprovação',
  suspended: 'Suspenso',
  rejected: 'Rejeitado',
  banned: 'Banido',
  inactive: 'Inativo',
};

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  pending: 'secondary',
  suspended: 'outline',
  rejected: 'destructive',
  banned: 'destructive',
  inactive: 'outline',
};

// Mapeia ícone + cor do ícone por status (texto sempre em foreground/preto)
function StatusIcon({ status }: { status: string }) {
  if (status === 'active') return <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--aff-success,142_71%_45%))]" />;
  if (status === 'pending') return <AlertCircle className="h-3.5 w-3.5 text-amber-500" />;
  if (status === 'suspended') return <Pause className="h-3.5 w-3.5 text-muted-foreground" />;
  return <AlertCircle className="h-3.5 w-3.5 text-destructive" />;
}

interface ProgramCardProps {
  program: HomeOverviewProgram;
}

function ProgramCard({ program: m }: ProgramCardProps) {
  const canWithdraw = m.available >= m.min_payout_amount && m.status === 'active';

  return (
    <Card className="overflow-hidden border-border/60 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold truncate">{m.program_name}</h3>
            {/* Linha secundária: mínimo de saque + total de indicações
                (movido do grid para liberar espaço para "Em processamento") */}
            <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
              <span className="inline-flex items-center gap-1">
                Mínimo de saque: {fmtBRL(m.min_payout_amount)}
                <KpiInfoTooltip
                  label="Mínimo de saque"
                  hint="Valor mínimo que você precisa acumular em saldo disponível para conseguir solicitar um saque neste programa."
                />
              </span>
              <span>·</span>
              <span>{fmtInt(m.referrals)} {m.referrals === 1 ? 'indicação' : 'indicações'}</span>
            </p>
          </div>
          {/* Status: ícone colorido + texto em foreground (sem fundo/badge escuro) */}
          <div className="flex items-center gap-1.5 shrink-0 text-xs font-medium text-foreground">
            <StatusIcon status={m.status} />
            {STATUS_LABEL[m.status] ?? m.status}
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 rounded-md bg-muted/40 border border-border/40">
          <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Wallet className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              Saldo disponível
              <KpiInfoTooltip
                label="Saldo disponível do programa"
                hint="Comissões aprovadas neste programa específico que ainda não foram solicitadas para saque."
              />
            </p>
            <p className="text-lg font-semibold tabular-nums">{fmtBRL(m.available)}</p>
          </div>
          {canWithdraw && (
            <Badge variant="secondary" className="shrink-0">
              Pode sacar
            </Badge>
          )}
        </div>

        {/* Grid de KPIs do programa.
            Ordem: Em processamento (saques pending/processing) → Ganho → Pago.
            "Indicações" foi movida para a linha secundária do cabeçalho. */}
        <div className="grid grid-cols-3 gap-2">
          <MiniKpi
            icon={<Clock className="h-3.5 w-3.5" />}
            label="Em processamento"
            value={fmtBRL(m.in_transit)}
            hint="Saques deste programa que você já solicitou e estão aguardando o financeiro processar."
          />
          <MiniKpi
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            label="Ganho"
            value={fmtBRL(m.earned)}
            hint="Tudo que você já ganhou neste programa ao longo da vida — soma de pago, disponível e em processamento."
          />
          <MiniKpi
            icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            label="Pago"
            value={fmtBRL(m.paid)}
            hint="Total que já saiu para sua conta neste programa, somando todos os saques concluídos."
          />
        </div>
      </CardContent>
    </Card>
  );
}

function MiniKpi({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  /** Texto opcional exibido em tooltip ao lado do label. */
  hint?: string;
}) {
  return (
    <div className="rounded-md border border-border/60 bg-card/50 px-2 py-1.5 min-w-0 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
      <div className="flex items-center gap-1 text-[10.5px] uppercase tracking-wide text-muted-foreground">
        {icon}
        <span className="truncate">{label}</span>
        {hint && <KpiInfoTooltip label={label} hint={hint} />}
      </div>
      <p className="text-[12.5px] font-semibold tabular-nums truncate mt-0.5">{value}</p>
    </div>
  );
}

interface AffiliateProgramsBreakdownCardProps {
  className?: string;
  /** Lista de programas (recomendado: vinda da Home overview). */
  programs?: HomeOverviewProgram[];
  isLoading?: boolean;
}

export function AffiliateProgramsBreakdownCard({
  className,
  programs: programsProp,
  isLoading: isLoadingProp,
}: AffiliateProgramsBreakdownCardProps) {
  // Fallback interno: se quem chamou não passar programs, busca a overview.
  const overview = useAffiliateHomeOverview();
  const programs = programsProp ?? overview.data?.programs;
  const isLoading = isLoadingProp ?? overview.isLoading;

  if (isLoading) {
    return (
      <section className={cn('space-y-3', className)}>
        <h2 className="text-base font-semibold">Meus programas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-3">
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-14 w-full" />
                <div className="grid grid-cols-3 gap-2">
                  <Skeleton className="h-10" />
                  <Skeleton className="h-10" />
                  <Skeleton className="h-10" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  if (!programs || programs.length === 0) {
    return null;
  }

  return (
    <section className={cn('space-y-3', className)} aria-label="Meus programas">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">
          {programs.length === 1 ? 'Meu programa' : `Meus programas (${programs.length})`}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {programs.map((m) => (
          <ProgramCard key={m.program_id} program={m} />
        ))}
      </div>
    </section>
  );
}
